import React from 'react';
import { ordersApi } from '@/services/api';
import { formatDate, formatDateTime, formatCurrency } from '@/utils/formatters';
import { InProgressBuyOrder, InProgressSellOrder } from '@/components/orders/types';
import { clientScopeLabel, fmt, pair } from './orderUtils';
import BaseInProgressOrders from '@/components/orders/BaseInProgressOrders';
import SchemeLabel from '@/components/orders/SchemeLabel';
import styles from '@/components/orders/OrderManagement.module.css';

interface InProgressOrdersProps {
  onProcessSuccess: (blockTrade: boolean) => void;
}

const InProgressOrders: React.FC<InProgressOrdersProps> = ({ onProcessSuccess }) => (
  <BaseInProgressOrders<InProgressBuyOrder, InProgressSellOrder>
    supportsBlockTrade
    fetchBuyOrders={() => ordersApi.getInProgressBuyOrders()}
    fetchSellOrders={() => ordersApi.getInProgressSellOrders()}
    processBuyOrders={(data) => ordersApi.processBuyOrders(data)}
    processSellOrders={(data) => ordersApi.processSellOrders(data)}
    onProcessSuccess={(blockTrade) => onProcessSuccess(blockTrade ?? false)}
    renderSharedHeaders={() => (
      <>
        <th>Order Date</th>
        <th>Order Type</th>
        <th>Scheme</th>
        <th>Clients</th>
        <th>Company</th>
      </>
    )}
    renderSharedBuyRowCells={(order) => (
      <>
        <td>{formatDate(order.order_date)}</td>
        <td><span className={styles['order-type-cell']}>{order.order_type_display ?? '—'}</span></td>
        <td><SchemeLabel scheme={order.scheme} /></td>
        <td>{clientScopeLabel(order.client_count)}</td>
        <td>{order.company.symbol}</td>
      </>
    )}
    renderSharedSellRowCells={(order) => (
      <>
        <td>{formatDate(order.order_date)}</td>
        <td><span className={styles['order-type-cell']}>{order.order_type_display ?? '—'}</span></td>
        <td><SchemeLabel scheme={order.scheme} /></td>
        <td>{clientScopeLabel(order.client_count)}</td>
        <td>{order.company.symbol}</td>
      </>
    )}
    renderBuyHeaders={() => (
      <>
        <th className={styles.num}>Limit Price</th>
        <th className={styles.num}>Max Holding %</th>
        <th className={styles.num}>Min Order Val</th>
        <th className={styles.num}>CL1 / CL2</th>
        <th className={styles.num}>Inc To 1/2</th>
        <th className={styles.num}>Inc Max 1/2</th>
        <th className={styles.num}>Inc By 1/2</th>
        <th>Buy Method</th>
      </>
    )}
    renderSellHeaders={() => (
      <>
        <th className={styles.num}>H1 / H2</th>
        <th className={styles.num}>Cond 1/2</th>
        <th className={styles.num}>Min Order Val</th>
        <th>Sell Method</th>
      </>
    )}
    renderBuyRow={(order) => (
      <>
        <td className={`${styles['text-mono']} ${styles.num}`}>{formatCurrency(order.limit_price, order.currency_code ?? order.scheme.currency)}</td>
        <td className={`${styles['text-mono']} ${styles.num}`}>{fmt(order.max_holding)}%</td>
        <td className={`${styles['text-mono']} ${styles.num}`}>{formatCurrency(order.min_order_val, order.scheme.currency)}</td>
        <td className={`${styles['text-mono']} ${styles.num}`}>{pair(order.cl1, order.cl2)}</td>
        <td className={`${styles['text-mono']} ${styles.num}`}>{pair(order.increase_to_1, order.increase_to_2)}</td>
        <td className={`${styles['text-mono']} ${styles.num}`}>{pair(order.increment_max_1, order.increment_max_2)}</td>
        <td className={`${styles['text-mono']} ${styles.num}`}>{pair(order.increase_by_1, order.increase_by_2)}</td>
        <td><span className={styles['method-cell']}>{order.buy_method_display ?? '—'}</span></td>
      </>
    )}
    renderSellRow={(order) => (
      <>
        <td className={`${styles['text-mono']} ${styles.num}`}>{pair(order.h1, order.h2)}</td>
        <td className={`${styles['text-mono']} ${styles.num}`}>{pair(order.condition_1, order.condition_2)}</td>
        <td className={`${styles['text-mono']} ${styles.num}`}>{formatCurrency(order.min_order_val, order.scheme.currency)}</td>
        <td><span className={styles['method-cell']}>{order.sell_method_display ?? '—'}</span></td>
      </>
    )}
    renderTrailingHeaders={() => (
      <>
        <th className={styles.num}>POV CAP %</th>
        <th>Vol Cap</th>
        <th>Created</th>
      </>
    )}
    renderTrailingBuyRowCells={(order) => (
      <>
        <td className={`${styles['text-mono']} ${styles.num}`}>{fmt(order.median_volume_pct)}%</td>
        <td>{order.apply_volume_cap ? 'Yes' : 'No'}</td>
        <td>{formatDateTime(order.created_at)}</td>
      </>
    )}
    renderTrailingSellRowCells={(order) => (
      <>
        <td className={`${styles['text-mono']} ${styles.num}`}>{fmt(order.median_volume_pct)}%</td>
        <td>{order.apply_volume_cap ? 'Yes' : 'No'}</td>
        <td>{formatDateTime(order.created_at)}</td>
      </>
    )}
  />
);

export default InProgressOrders;